import React from 'react';
import moment from 'moment';
import { Tooltip } from 'antd';
import { fluid, fluidVh } from 'components/UserDashboardV2/utils/responsive';
import { getAttendanceBgColor, formatClockTime } from './utils';

// Student days: the tooltip shows the status (students have no clock times).
const STUDENT_STATUS_LABELS = {
    present: "Present",
    absent: "Absent",
    partial: "Partially present",
    partiallypresent: "Partially present",
    halfday: "Partially present",
    noclass: "No class",
    classcancelled: "Class cancelled",
    cancelled: "Class cancelled",
};

const AttendanceCalendarStrip = ({
    calendarScrollContainerRef,
    currentMonthRef,
    visibleMonthColumns,
    showAttendanceBySubject,
    dynamicCalendarMonths,
    isStaffRoute,
    isTeacher,
    handleAttendanceDateClick,
    onMarkAttendance,
}) => {
    // Same window the Mark attendance drawer allows: 3 days ago .. yesterday.
    const markFrom = moment().subtract(3, "days").startOf("day");
    const markTo = moment().subtract(1, "days").endOf("day");
    return (
        <div
            ref={calendarScrollContainerRef}
            className="staff-profile-info-scroll"
            style={{
                display: "grid",
                gridAutoFlow: "column",
                gridAutoColumns: `calc((100% - ${fluid(20)} * ${visibleMonthColumns - 1}) / ${visibleMonthColumns})`,
                gap: fluid(20),
                width: "100%",
                minWidth: 0,
                maxHeight: showAttendanceBySubject ? undefined : fluidVh(340),
                overflowX: "auto",
                overflowY: showAttendanceBySubject ? "hidden" : "auto",
                paddingBottom: fluid(4),
                scrollbarWidth: "none",
                msOverflowStyle: "none",
            }}
        >
            {dynamicCalendarMonths.map((month, idx) => {
                const isCurrentMonth = month.monthIndex === moment().month();
                return (
                    <div
                        key={idx}
                        ref={isCurrentMonth ? currentMonthRef : null}
                        style={{ minWidth: 0 }}
                    >
                        <span style={{ display: "block", fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#191919", marginBottom: fluid(12) }}>
                            {month.title}
                        </span>
                        {/* Days of week header */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: fluid(4), marginBottom: fluid(8), justifyItems: "center" }}>
                            {["M", "T", "W", "T", "F", "S", "S"].map((dayName, dIdx) => (
                                <span key={dIdx} style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, color: "#AEAEAE", textAlign: "center" }}>
                                    {dayName}
                                </span>
                            ))}
                        </div>
                        {/* Day cells grid */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: fluid(4) }}>
                            {month.days.map((dayItem, dayIdx) => {
                                const isOffDay = dayItem.isOffDay;
                                const statusKey = String(dayItem.status || "").toLowerCase().replace(/[\s_-]/g, "");
                                // Student "Class cancelled" day.
                                const isClassCancelled = !isStaffRoute && !isOffDay && (statusKey === "classcancelled" || statusKey === "cancelled");
                                const isHolidayOrLeaveStatus = statusKey === "holiday" || statusKey === "leave" || statusKey === "onleave";
                                // Holiday / Leave — staff only.
                                const isHoliday = isStaffRoute && !isOffDay && statusKey === "holiday";
                                const isLeave = isStaffRoute && !isOffDay && (statusKey === "leave" || statusKey === "onleave");
                                // Offday / Holiday / Leave show a simple label tooltip instead of clock times.
                                const studentStatusLabel = !isStaffRoute && !isTeacher && !dayItem.empty && !dayItem.isFuture
                                    ? (STUDENT_STATUS_LABELS[statusKey] || null) : null;
                                // Holiday: just its title (falls back to "Holiday").
                                const dayLabel = isOffDay ? "Day off" : isHoliday ? (dayItem.holidayTitle || "Holiday") : isLeave ? (dayItem.leaveType ? `${dayItem.leaveType} leave` : "Leave") : studentStatusLabel;
                                const showTooltip = Boolean(dayLabel) || ((isStaffRoute || isTeacher) && !dayItem.empty && !dayItem.isFuture);
                                // Every past/today date is clickable — including offdays, holidays and leaves.
                                const isClickable = !dayItem.empty && Boolean(dayItem.dateStr) && !dayItem.isFuture;
                                const inStr = dayItem.clockIn ? formatClockTime(dayItem.clockIn) : null;
                                const outStr = dayItem.clockOut ? formatClockTime(dayItem.clockOut) : null;
                                const clockRangeStr = (inStr || outStr)
                                    ? `${inStr || "--:--"} - ${outStr || "--:--"}`
                                    : "-";
                                // Absent in the last 3 days (staff) — offer "Mark attendance".
                                const canMarkAttendance = isStaffRoute && Boolean(onMarkAttendance) && !dayItem.empty
                                    && !isOffDay && statusKey === "absent" && Boolean(dayItem.dateStr)
                                    && moment(dayItem.dateStr, "YYYY-MM-DD").isBetween(markFrom, markTo, null, "[]");
                                // Absent: just "Absent" (+ "Mark attendance" for the last 3 days).
                                const isAbsent = !isOffDay && statusKey === "absent";
                                const markAttendanceLink = canMarkAttendance && (
                                    <div
                                        role="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onMarkAttendance(dayItem.dateStr);
                                        }}
                                        style={{
                                            marginTop: fluid(6),
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            color: "#B9A7FF",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Mark attendance
                                    </div>
                                );

                                const tooltipTitle = dayLabel ? (
                                    <div style={{ textAlign: "center", padding: `${fluid(4)} ${fluid(8)}`, whiteSpace: "nowrap" }}>
                                        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 500, color: "#FFFFFF" }}>
                                            {dayLabel}
                                        </div>
                                        {/* Holiday title is now the main line — second line no longer needed. */}
                                        {/* {isHoliday && dayItem.holidayTitle && (
                                            <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(12), color: "#B5B5B5", marginTop: fluid(2) }}>
                                                {dayItem.holidayTitle}
                                            </div>
                                        )} */}
                                    </div>
                                ) : isAbsent ? (
                                    <div style={{ textAlign: "center", padding: `${fluid(4)} ${fluid(8)}`, whiteSpace: "nowrap" }}>
                                        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 500, color: "#FFFFFF" }}>
                                            Absent
                                        </div>
                                        {markAttendanceLink}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: "center", padding: `${fluid(4)} ${fluid(8)}`, whiteSpace: "nowrap" }}>
                                        <div style={{
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: fluid(16),
                                            fontWeight: 500,
                                            color: "#B5B5B5",
                                            marginBottom: fluid(4),
                                            lineHeight: 1.2,
                                        }}>
                                            Clock in - Clock out
                                        </div>
                                        <div style={{
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: fluid(18),
                                            fontWeight: 600,
                                            color: "#FFFFFF",
                                            lineHeight: 1.2,
                                        }}>
                                            {clockRangeStr}
                                        </div>
                                    </div>
                                );

                                const cellNode = (
                                    <div
                                        onClick={() => {
                                            if (isClickable) {
                                                handleAttendanceDateClick(dayItem.dateStr);
                                            }
                                        }}
                                        style={{
                                            aspectRatio: "1 / 1",
                                            borderRadius: fluid(6),
                                            backgroundColor: dayItem.empty
                                                ? "transparent"
                                                : isOffDay
                                                    ? "#F2F2F2"
                                                    : isClassCancelled
                                                    ? "#C3E9F3"
                                                    : ((dayItem.isFuture && !isHoliday && !isLeave) || (!isStaffRoute && isHolidayOrLeaveStatus)
                                                        ? "transparent"
                                                        : getAttendanceBgColor(dayItem.status)),
                                            border: (!dayItem.isFuture && dayItem.selected) ? "2px solid #594099" : "none",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxSizing: "border-box",
                                            cursor: isClickable ? "pointer" : "default",
                                        }}
                                    >
                                        {!dayItem.empty && (
                                            <span style={{
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: fluid(14),
                                                fontWeight: (!dayItem.isFuture && dayItem.selected) ? 600 : 400,
                                                // Future dates look disabled; every other date stays black.
                                                color: dayItem.isFuture ? "#AEAEAE" : "#000000"
                                            }}>
                                                {dayItem.day}
                                            </span>
                                        )}
                                    </div>
                                );

                                if (showTooltip) {
                                    return (
                                        <Tooltip
                                            key={dayIdx}
                                            title={tooltipTitle}
                                            placement="top"
                                            // Sit closer to the date (antd's default leaves a large gap).
                                            align={{ offset: [0, 2] }}
                                            overlayInnerStyle={{
                                                borderRadius: fluid(8),
                                                padding: `${fluid(6)} ${fluid(10)}`,
                                            }}
                                        >
                                            {cellNode}
                                        </Tooltip>
                                    );
                                }

                                return (
                                    <React.Fragment key={dayIdx}>
                                        {cellNode}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AttendanceCalendarStrip;
